<template>
    <div>
        <!-- rating modal -->
        <Modal :isOpen="isModalOpen" :changeModalState="changeModalState" :disableScrolling="true"
            :modalTitle="$t('user.rate_store_modal_title')" centeredTitle smallTitle :size="'4xl'"
            :hideHeadersBorder="true">
            <div
                class="relative pb-6 md:pb-7 px-4 md:px-6 lg:px-8 xl:px-10 2xl:px-12 3xl:px-13 overscroll-none overflow-y-auto min-h-[calc(15vh)] max-h-[80vh]">
                <!-- shows when loading -->
                <div v-if="isLoading || isUpdateDataStore" class="section-loader bg-white z-[1]">
                    <div class="lds-dual-ring"></div>
                </div>
                <div v-else class="relative">
                    <!-- comments - feedback -->
                    <!-- v-if="areCommentsAvailable"  -->
                    <div class="w-full flex lg:justify-end pt-5">
                        <div class="w-full">
                            <Feedbacks :hasReachedFullPages="currentPage >= maxPages"
                                :loadMoreComments="() => fetchStoreComments({ loadMore: true })" :isLoading="isLoading"
                                :isLoadingMore="isLoadingMore" :isDeletingRate="isDeletingRate"
                                :isReportingRate="isReportingRate" :items="comments"
                                :confirmDeletingRate="confirmDeletingRate"
                                :confirmReportingRate="confirmReportingRate"
                                :handleLikeClick="handleLikeClick"
                                :onEditRatingOpenModal="onEditRatingOpenModal"
                                :typeRate="typeRate" />
                        </div>
                    </div>
                </div>
            </div>
        </Modal>
    </div>
</template>

<script>
// Icons
import StarRating from "@/components/generic/StarRating.vue";
import Star from "@/assets/shared/icons/Star.vue";
import Feedbacks from "../feedbacks/Feedbacks.vue";
import {
    MAINROLES,
    APIREQUESTS,
    constructQueryString,
} from "@/helpers";
import { Modal, SelectableStarsRating } from "@/components";
import starIcon from "@/assets/images/star.svg";

export default {
    name: "StoreRate",
    data() {
        return {
            starIcon,
            isLoading: false,
            isLoadingMore: false,
            isDeletingRate: false,
            isReportingRate: false,
            isRatingFormSubmitted: false,
            showFormShadows: false,
            currentPage: 1,
            maxPages: 10,
            rateForm: {
                selectedRating: 0,
                comment: "",
            },
            hasReachedFullPages: false,
            comments: [],
        };
    },
    components: {
        StarRating,
        Feedbacks,
        Star,
        Modal,
        SelectableStarsRating
    },
    computed: {
        areCommentsAvailable() {
            return this.ratingData?.ratings_count > 0;
        },
        typeRate(){
            return 'stores';
        }
    },
    props: {
        isModalOpen: {
            type: Boolean,
            default: false,
            required: true,
        },
        isUpdateDataStore: {
            type: Boolean,
            default: false,
            required: true,
        },
        changeModalState: {
            type: Function,
            required: true,
        },
        directUserToLoginPage: {
            type: Function,
            required: true,
        },
        refreshStoreData: {
            type: Function,
            required: true,
        },
        ratingData: {
            type: Object,
            required: true,
            default: () => ({}),
        },
        onEditRatingOpenModal: {
            type: Function,
            required: false
        },
    },
    methods: {
        handleLikeClick(item) {
            this.isLoading = true;
            const formData = new FormData();
            formData.append('rating_id', item.item.id);

            this.makeRequest({
                owner: MAINROLES.user,
                url: `${item.type === 'like' ? APIREQUESTS.user.likeStoreComment.url : APIREQUESTS.user.dislikeStoreComment.url}`,
                method: item.type === 'like' ? APIREQUESTS.user.likeStoreComment.method : APIREQUESTS.user.dislikeStoreComment.method,
                formData: formData
            }).then(res => {
                if (res?.status) {
                    this.fetchStoreComments({ isInitialLoading: true });
                } else {
                    this.notify({ type: 'error', msg: this.$t('general.error_occurred') });
                }
            }).catch(err => {
                this.notify({ type: 'error', msg: this.$t('general.error_occurred') });
            }).finally(() => {
                this.isLoading = false;
            });
        },
        fetchStoreComments({ loadMore = false }) {
            return new Promise((resolve, reject) => {
                if (loadMore) {
                    this.isLoadingMore = true;
                } else {
                    this.isLoading = true;
                }

                const storeId = this.ratingData?.store?.id;
                const nextPage = +this.currentPage + 1;
                const queryString = constructQueryString([
                    ...(loadMore ? [{ key: "page", value: nextPage }] : []),
                    { key: "store_id", value: storeId },
                ]);

                this.makeRequest({
                    ...(this.isUserLoggedIn && { owner: MAINROLES.user }),
                    url: `${APIREQUESTS.user.getStoreRatings.url}${queryString}`,
                    method: APIREQUESTS.user.getStoreRatings.method,
                })
                    .then((res) => {
                        if (res?.status) {
                            resolve();
                            const receivedDataArray = res.data || [];
                            let newCommentsArray = [];
                            if (
                                loadMore &&
                                this.areCommentsAvailable > 0 &&
                                this.comments?.length > 0
                            ) {
                                newCommentsArray = [...this.comments, ...receivedDataArray];
                            } else {
                                newCommentsArray = receivedDataArray;
                            }
                            this.comments = newCommentsArray;
                            // for load more
                            this.currentPage = parseInt(res.current_page);
                            this.maxPages = parseInt(res.pages_total);
                        } else {
                            reject();
                            this.notify({
                                type: "error",
                                msg: res?.message || this.$t("general.failed"),
                            });
                            this.comments = [];
                        }
                    })
                    .catch((err) => {
                        this.notify({
                            type: "error",
                            msg: err?.data?.message || this.$t("general.failed"),
                        });
                    })
                    .finally(() => {
                        reject();
                        this.isLoadingMore = false;
                        this.isLoading = false;
                    });
            });
        },
        addRating() {
            this.isLoading = true;
            this.isRatingFormSubmitted = true;
            const storeId = this.ratingData?.store?.id;
            if (this.rateForm?.selectedRating <= 0) {
                this.isLoading = false;
                return;
            }
            this.makeRequest({
                owner: MAINROLES.user,
                url: APIREQUESTS.user.addStoreRating.url,
                method: APIREQUESTS.user.addStoreRating.method,
                formData: {
                    store_id: storeId,
                    rating: +this.rateForm.selectedRating,
                    comment: this.rateForm.comment,
                },
            })
                .then((res) => {
                    if (res?.status) {
                        this.isRatingFormSubmitted = false;
                        // TODO: modify the store data instead of calling store date APIs
                        // update only the value of these following keys:
                        // 1- ratings_avg
                        // 2- ratings_count
                        this.refreshStoreData()?.then(() => {
                            this.changeModalState(false);
                        });

                        const myNewlyAddedComment = res?.data || {};
                        this.comments = [myNewlyAddedComment, ...(this.comments || [])];

                        this.rateForm = {
                            selectedRating: 0,
                            comment: "",
                        };
                    } else {
                        this.notify({
                            type: "error",
                            msg: res?.message || this.$t("general.failed"),
                        });
                    }
                })
                .catch((err) => {
                    this.notify({
                        type: "error",
                        msg: err?.data?.message || this.$t("general.failed"),
                    });
                })
                .finally(() => {
                    this.isLoading = false;
                });
        },
        confirmReportingRate({rateForm, item}){
            this.isReportingRate = true;
            const ratingId = item?.id;
            // adjust the endpoint and formData
            if (!ratingId) {
                return;
            }
            const bodyRequest = {
                rating_id: ratingId,
                comment: rateForm.comment
            }

            this.makeRequest({
                owner: MAINROLES.user,
                url: APIREQUESTS.user.reportStoreRating.url,
                method: APIREQUESTS.user.reportStoreRating.method,
                formData: JSON.stringify(bodyRequest)
            })
                .then(res => {
                if (res?.status) {
                    this.notify({
                    type: "success",
                    msg: res?.data?.message || this.$t("general.succeeded")
                    });
                    this.refreshStoreData();
                    this.changeModalState(false);
                } else {
                    this.notify({
                    type: "error",
                    msg: res?.message || this.$t("general.failed")
                    });
                }
                })
                .catch(err => {
                this.notify({
                    type: "error",
                    msg: err?.data?.message || this.$t("general.failed")
                    });
                })
                .finally(() => {
                this.isReportingRate = false;
                });
        },
        confirmDeletingRate(ratingId) {
            this.isDeletingRate = true;
            if (!ratingId) {
                return;
            }
            this.makeRequest({
                owner: MAINROLES.user,
                url: `${APIREQUESTS.user.deleteStoreRating.url}/${ratingId}`,
                method: APIREQUESTS.user.deleteStoreRating.method,
            })
                .then((res) => {
                    if (res?.status) {
                        // TODO: remove item from the data instead of calling the store date APIs
                        // update only the value of these following keys:
                        // 1- ratings_avg
                        // 2- ratings_count
                        this.refreshStoreData();
                        this.comments = this.comments?.filter(
                            (comment) => comment.id != ratingId
                        );
                        ratingId = null;
                    } else {
                        this.notify({
                            type: "error",
                            msg: res?.message || this.$t("general.failed"),
                        });
                    }
                })
                .catch((err) => {
                    this.notify({
                        type: "error",
                        msg: err?.data?.message || this.$t("general.failed"),
                    });
                })
                .finally(() => {
                    this.isDeletingRate = false;
                    this.changeModalState(false);
                });
        },
    },
    watch: {
        isModalOpen: {
            immediate: true,
            handler(val) {
                if (typeof val === "boolean") {
                    if (val) {
                        // if (this.areCommentsAvailable) {
                            this.fetchStoreComments({});
                        // } else {
                        //     this.currentPage = 1;
                        //     this.maxPages = 1;
                        // }
                    } else {
                        // reset the rateForm when the modal is closed
                        Object.keys(this.rateForm || {}).forEach((key) => {
                            this.rateForm[key] = "";
                        });
                        this.isRatingFormSubmitted = false;
                    }
                }
            },
        },
    },
};
</script>